import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Pagination,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { adminService } from "../../services/adminService";
import ReportFilters, { today, monthAgo } from "./ReportFilters";
import { formatShowDateTimeIST } from "../../utils/dateTimeIST";
import toast from "react-hot-toast";

const fmt = (v: any) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const ManualRefundsReport: React.FC = () => {
  const [filters, setFilters] = useState({
    dateFrom: monthAgo,
    dateTo: today,
    cinemaId: "",
    refundMode: "",
  });
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cinemas, setCinemas] = useState<Array<{ id: string; name: string }>>(
    [],
  );

  const limit = 50;

  useEffect(() => {
    adminService
      .getCinemas()
      .then((r: any) => {
        const list = r?.data || [];
        setCinemas(
          list.map((c: any) => ({ id: c.vistacinemaid || c.id, name: c.name })),
        );
      })
      .catch(() => setCinemas([]));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const r: any = await adminService.getManualRefundsReport({
        ...filters,
        page,
        limit,
      });
      setRows(r?.data || []);
      setTotal(r?.pagination?.total || 0);
    } catch (e: any) {
      setError(e?.message || "Failed to load manual refunds report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onApply = () => {
    setPage(1);
    fetchData();
  };

  const onReset = () => {
    setFilters({
      dateFrom: monthAgo,
      dateTo: today,
      cinemaId: "",
      refundMode: "",
    });
    setPage(1);
    setTimeout(fetchData, 0);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await adminService.exportManualRefundsReport(filters);
      toast.success("Excel downloaded");
    } catch (e: any) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <Helmet>
        <title>Manual Refunds Report - MyCinemas Admin</title>
      </Helmet>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">Manual Refunds Report</h2>
          <small className="text-muted">
            Cash refunds issued at the cinema counter. Populated by the Manual
            Refund workflow in Bookings → 3-dot menu.
          </small>
        </div>
        <Badge bg="warning" text="dark" className="p-2">
          {total.toLocaleString("en-IN")} total
        </Badge>
      </div>

      <ReportFilters
        {...filters}
        cinemas={cinemas}
        onChange={(p) => setFilters((prev) => ({ ...prev, ...p }))}
        onApply={onApply}
        onReset={onReset}
        onExport={onExport}
        loading={loading}
        exporting={exporting}
        showRefundMode
      />

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="mb-0">
            <thead className="table-light">
              <tr>
                <th>MR Date</th>
                <th>Cinema</th>
                <th>Bkg ID</th>
                <th>Movie</th>
                <th>Show Date &amp; Time</th>
                <th>Orig Tickets</th>
                <th className="text-end">Orig Amount</th>
                <th>MR Qty</th>
                <th className="text-end">MR Amount</th>
                <th>Refund Mode</th>
                <th>Customer Contact</th>
                <th>Refund Reason</th>
                <th>Initiated By</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={13} className="text-center py-4">
                    <Spinner size="sm" /> Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={13} className="text-center py-4 text-muted">
                    No manual refunds found for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r: any) => (
                  <tr key={r.bookingId}>
                    <td className="small">{formatShowDateTimeIST(r.mrDate)}</td>
                    <td className="small">{r.cinema}</td>
                    <td className="font-monospace small">{r.bookingId}</td>
                    <td className="small">{r.movie}</td>
                    <td className="small">{formatShowDateTimeIST(r.showDateTime)}</td>
                    <td className="small">{r.originalTickets}</td>
                    <td className="text-end small">
                      {fmt(r.originalAmount)}
                    </td>
                    <td className="small">{r.mrQty}</td>
                    <td className="text-end small fw-bold">
                      {fmt(r.mrAmount)}
                    </td>
                    <td className="small">
                      <Badge bg="secondary">{r.refundMode || "-"}</Badge>
                    </td>
                    <td className="small">
                      {[r.customerMobile, r.customerEmail]
                        .filter(Boolean)
                        .join(" / ") || "-"}
                    </td>
                    <td className="small">{r.refundReason || "-"}</td>
                    <td className="small">{r.initiatedBy || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First
              disabled={page === 1}
              onClick={() => setPage(1)}
            />
            <Pagination.Prev
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />
            <Pagination.Item active>{page}</Pagination.Item>
            <Pagination.Next
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
            <Pagination.Last
              disabled={page === totalPages}
              onClick={() => setPage(totalPages)}
            />
          </Pagination>
        </div>
      )}
    </>
  );
};

export default ManualRefundsReport;
